import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useTable, Column } from "react-table";
import axiosInstance from "../../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import { DocumentArrowDownIcon, SquaresPlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { QRCodeCanvas } from "qrcode.react";
import toast from "react-hot-toast";
import {
  addQrPageToPdf,
  createQrPdfDocument,
  QR_CANVAS_PX,
  QR_PDF_JPEG_QUALITY,
  waitForQrCanvasRender,
} from "../../utils/qrPdfConfig";

interface QrLote {
  id_qr: number;
  id_lote_qr: number;
  sku: string;
  id_qr_inicio: number;
  id_qr_fin: number;
  fecha: string;
}

const QrList: React.FC = () => {
  const [qrList, setQrList] = useState<QrLote[]>([]);
  const [qrData, setQrData] = useState<{ sku: string; lote: string; numero_de_serie: string; id_qr: string  }[]>([]);
  const qrCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const navigate = useNavigate();

  const fetchQrList = async () => {
    try {
      const response = await axiosInstance.get("/qr/list");
      const formattedData = response.data.map((qrLote: QrLote) => ({
        ...qrLote,
        fecha: new Date(qrLote.fecha).toISOString().split("T")[0], 
      }));
  
      setQrList(formattedData);
    } catch (error) {
      console.error("Error obteniendo la lista de lotes:", error);
    }
  };
  

  
  useEffect(() => {
    fetchQrList();
  }, []);

  const handleGeneratePdf = useCallback(async (id_lote_qr: number, sku: string, id_qr_inicio: number, id_qr_fin: number) => {
    try {
      const loadingToast = toast.loading('Generando PDF...', { position: 'top-center' });

      // Generar los IDs QR en el rango especificado
      const qrDetails = Array.from({ length: id_qr_fin - id_qr_inicio + 1 }, (_, i) => ({
        sku,
        lote: id_lote_qr.toString(),
        numero_de_serie: (id_qr_inicio + i).toString(),
        id_qr: (id_qr_inicio + i).toString()
      }));

      if (!qrDetails || qrDetails.length === 0) {
        toast.error("No hay códigos QR para este lote.", { position: 'top-center' });
        return;
      }

      flushSync(() => setQrData(qrDetails));
      await waitForQrCanvasRender();

      const doc = createQrPdfDocument();

      for (let i = 0; i < qrDetails.length; i++) {
        const qrCanvas = qrCanvasRefs.current[i];
        if (!qrCanvas) continue;

        const qrImgData = qrCanvas.toDataURL("image/jpeg", QR_PDF_JPEG_QUALITY);
        addQrPageToPdf(doc, qrImgData, qrDetails[i].id_qr, i === 0);

        qrCanvasRefs.current[i] = null;
      }

      // Limpiar el estado después de generar el PDF
      setQrData([]);
      qrCanvasRefs.current = [];

      doc.save(`QRs_Lote_${id_lote_qr}.pdf`);
      
      toast.success('PDF generado exitosamente', { id: loadingToast, position: 'top-center' });
    } catch (error) {
      console.error("Error generando el PDF:", error);
      toast.error("Hubo un error generando el PDF.", { position: 'top-center' });
    }
  }, []);
  

  const handleDelete = useCallback(async (id_lote_qr: number) => {
    const confirmDelete = () => {
      toast((t) => (
        <div className="flex flex-col gap-2">
          <p className="text-gray-800">¿Estás seguro que deseas eliminar este lote QR?</p>
          <p className="text-red-500 text-sm">Esta acción no se puede deshacer.</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                deleteLote(id_lote_qr);
              }}
              className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
            >
              Eliminar
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancelar
            </button>
          </div>
        </div>
      ), { position: 'top-center' });
    };

    const deleteLote = async (id: number) => {
      try {
        await axiosInstance.delete(`/qr/${id}`);
        toast.success("Lote QR eliminado exitosamente", { position: 'top-center' });
        fetchQrList(); // Recargar la lista después de eliminar
      } catch (error) {
        console.error("Error eliminando el lote QR:", error);
        toast.error("Error al eliminar el lote QR", { position: 'top-center' });
      }
    };

    confirmDelete();
  }, []);

  const columns = useMemo<Column<QrLote>[]>(
    () => [
      { Header: "ID Lote QR", accessor: "id_lote_qr" as const },
      { Header: "SKU", accessor: "sku" as const },
      { Header: "ID QR Inicio", accessor: "id_qr_inicio" as const },
      { Header: "ID QR Fin", accessor: "id_qr_fin" as const },
      { Header: "Fecha", accessor: "fecha" as const },
      {
        Header: "Acciones",
        accessor: "acciones" as unknown as keyof QrLote,
        Cell: ({ row }: { row: any }) => (
          <div className="flex space-x-2">
            <button 
              onClick={() => handleGeneratePdf(
                row.original.id_lote_qr, 
                row.original.sku,
                row.original.id_qr_inicio,
                row.original.id_qr_fin
              )}
              className="text-blue-500 hover:text-blue-700" 
              title="Generar PDF"
            >
              <DocumentArrowDownIcon className="h-6 w-6" />
            </button>
            <button 
              onClick={() => handleDelete(row.original.id_lote_qr)}
              className="text-red-500 hover:text-red-700" 
              title="Eliminar"
            >
              <TrashIcon className="h-6 w-6" />
            </button>
          </div>
        ),
      },      
    ],
    [handleDelete, handleGeneratePdf]
  );

  const tableInstance = useTable({ columns, data: qrList });
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  return (
    <div className="overflow-x-auto m-8">
      <h2 className="text-2xl font-bold mb-4">{/* Lista de Lotes QR */}</h2>
      <button className="bg-custom-blue text-white px-4 py-2 rounded mb-4 flex" onClick={() => navigate('/qr')}>
            <SquaresPlusIcon className="h-6 w-6 text-white-500" />
            Generar QRs
          </button>
      <table {...getTableProps()} className="w-full table-auto divide-y divide-gray-200">
        <thead className="bg-gray-50">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={row.id}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} key={cell.column.id} className="px-6 py-4 whitespace-nowrap">
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="hidden">
        {qrData.map((qr, index) => (
          <div key={index}>
            <QRCodeCanvas 
              value={`${qr.id_qr}`} 
              size={QR_CANVAS_PX}
              level="H" // Aumentado el nivel de corrección de errores para mejor calidad
              ref={(el) => (qrCanvasRefs.current[index] = el)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default QrList;
